/* istanbul ignore file */

import type { ComponentProps } from 'react';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { type LayoutChangeEvent, StyleSheet, View } from 'react-native';

import { useSelector } from '@xstate5/react';

import { findCenterPoint, findCenterRegion, theme, useResponsive, useIsFocused } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useCustomer } from '../../../../context/Customer';
import { useIsLiquidGlassEnabled } from '../../../../hooks';
import { LocationsMap } from '../../../LocationsMap';
import { useLocationSearchFlags } from '../../hooks/useLocationSearchFlags';
import { useLocationSearchHelpers } from '../../hooks/useLocationSearchHelpers';
import { useLocationSearchVisibleLocations } from '../../hooks/useLocationSearchVisibleLocations';
import { type LocationSearchService } from '../../machine';
import {
  generateMapPinsForLocations,
  getActiveLocationsCoordinates,
} from './LocationSearchMap.utils';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchMap = memo((props: LocationSearchMapProps) => {
  const { locationSearchActorRef, topOverlayInset } = props;

  const isScreenFocused = useIsFocused();
  const { customer } = useCustomer();
  const { match, minWidth } = useResponsive();

  const isLocationSelectionUpdateEnabled = useFeatureFlag('CELS-5577-order-location-selection-ui-update-enabled'); // prettier-ignore
  const isLiquidGlassEnabled = useIsLiquidGlassEnabled();

  const locationSearchType = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.locationSearchType,
  );

  const deliveryLocation = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.deliveryLocation,
  );

  const currentPosition = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.currentPosition,
  );

  const focusedPinId = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.focusedPinId,
  );

  const nearbyLocations = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.nearbyLocations,
  );

  const selectedPredictionLocation = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.baseLocation,
  );

  const selectedSearchArea = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.selectedSearchArea,
  );

  const visibleLocations = useLocationSearchVisibleLocations({ locationSearchActorRef });

  const { isLoadingLocations, shouldRenderRecentAndNearbyLocations } = useLocationSearchFlags({
    locationSearchActorRef,
  });

  const {
    searchBasedOnCurrentPosition,
    searchLocationsByArea,
    setLocationSearchArea,
    setFocusedLocation,
    navigateToSelectedLocation,
    navigateToSelectedDeliveryLocation,
    navigateToRecentOrNearbyLocation,
  } = useLocationSearchHelpers({ locationSearchActorRef });

  const customerName = customer?.firstName ?? '';
  const isDeliveryLocationSearchType = locationSearchType === 'delivery';
  const variation = shouldRenderRecentAndNearbyLocations ? 'recent-and-nearby' : 'standard';
  const locations = shouldRenderRecentAndNearbyLocations ? nearbyLocations : visibleLocations;

  // ─── State ───────────────────────────────────────────────────────────

  const [mapDimensions, setMapDimensions] = useState({ width: 0, height: 0 });

  // ─── Helpers ─────────────────────────────────────────────────────────

  const defaultRegion = match([US_REGION_XS, US_REGION_SM]);

  const updateMapDimensions = useCallback((event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;

    setMapDimensions({ width, height });
  }, []);

  const clearFocusedPinId = useCallback(() => {
    setFocusedLocation(undefined);
  }, [setFocusedLocation]);

  const handleOnCalloutPress = useCallback(
    ({ restaurantSlug }: PinLocationDetails) => {
      if (!restaurantSlug) return;

      navigateToSelectedLocation({ restaurantSlug }, { entryPoint: 'pin' });
    },
    [navigateToSelectedLocation],
  );

  const handleOnDeliveryCalloutPress = useCallback(
    ({ restaurantSlug }: PinLocationDetails) => {
      if (!restaurantSlug) return;

      navigateToSelectedDeliveryLocation({ restaurantSlug }, { entryPoint: 'pin' });
    },
    [navigateToSelectedDeliveryLocation],
  );

  const handleRecentOrNearbyLocationCalloutPress = useCallback(
    (locationDetails: PinLocationDetails) => {
      const { restaurantId } = locationDetails;

      if (!restaurantId) return;

      const pickupLocations = locations?.pickup ?? [];
      const outpostLocations = locations?.outpost ?? [];

      const allLocations = [...pickupLocations, ...outpostLocations];
      const targetLocation = allLocations.find(
        (location) => location.restaurantId === restaurantId,
      );

      if (!targetLocation) return;

      navigateToRecentOrNearbyLocation({
        location: targetLocation,
        options: { entryPoint: 'pin' },
      });
    },
    [locations?.outpost, locations?.pickup, navigateToRecentOrNearbyLocation],
  );

  const onCalloutPress = useCallback(
    (locationDetails: PinLocationDetails) => {
      if (variation === 'recent-and-nearby') {
        handleRecentOrNearbyLocationCalloutPress(locationDetails);

        return;
      }

      if (isDeliveryLocationSearchType) {
        handleOnDeliveryCalloutPress(locationDetails);

        return;
      }

      handleOnCalloutPress(locationDetails);
    },
    [
      handleOnCalloutPress,
      handleOnDeliveryCalloutPress,
      handleRecentOrNearbyLocationCalloutPress,
      isDeliveryLocationSearchType,
      variation,
    ],
  );

  const handleOnSearchAreaPress = isDeliveryLocationSearchType ? undefined : searchLocationsByArea;

  // ─── Derived Data ────────────────────────────────────────────────────

  const activeLocationsCoordinates = useMemo(() => {
    const activeLocations = getActiveLocationsCoordinates({
      locationSearchType,
      locations,
      deliveryLocation,
    });

    // include customer's current position (if available)
    if (currentPosition && !selectedPredictionLocation) {
      const customerCurrentPosition = {
        lat: currentPosition.latitude,
        lng: currentPosition.longitude,
        isCustomerPosition: true,
      };

      return [...activeLocations, customerCurrentPosition];
    }

    return activeLocations;
  }, [
    currentPosition,
    deliveryLocation,
    locationSearchType,
    locations,
    selectedPredictionLocation,
  ]);

  const pins = useMemo(() => {
    return generateMapPinsForLocations({
      locationSearchType,
      selectedPredictionLocation,
      customerName,
      locations,
      deliveryLocation,
    });
  }, [customerName, deliveryLocation, locationSearchType, locations, selectedPredictionLocation]);

  const customerPositionPin = useMemo(() => {
    if (!currentPosition) return;

    return {
      id: 'customer',
      name: customerName,
      lat: currentPosition.latitude,
      lng: currentPosition.longitude,
      isCustomer: true,
    } as const;
  }, [currentPosition, customerName]);

  const searchAreaRegion = useMemo(() => {
    const shouldUseSearchAreaBounds = locationSearchType !== 'delivery' && selectedSearchArea;

    if (!shouldUseSearchAreaBounds) return;

    const center = findCenterPoint([selectedSearchArea.topLeft, selectedSearchArea.bottomRight]); // prettier-ignore

    return {
      lat: center.lat,
      lng: center.lng,
      topLeft: selectedSearchArea.topLeft,
      bottomRight: selectedSearchArea.bottomRight,
    };
  }, [locationSearchType, selectedSearchArea]);

  const region = useMemo(() => {
    if (activeLocationsCoordinates.length === 0) return;

    const pinsRegion = findCenterRegion(activeLocationsCoordinates, mapDimensions);

    const isValidRegion = !Number.isNaN(pinsRegion.lat) && !Number.isNaN(pinsRegion.lng);

    return isValidRegion ? pinsRegion : defaultRegion;
  }, [activeLocationsCoordinates, mapDimensions, defaultRegion]);

  // ─────────────────────────────────────────────────────────────────────

  const isAutofitEnabled = !searchAreaRegion;
  const isOffsetEnabled = !searchAreaRegion;

  const shouldInsetMapForLocationContainer = isLocationSelectionUpdateEnabled && minWidth.isSM;
  const shouldInsetMapForLocationHeader =
    isLocationSelectionUpdateEnabled && !minWidth.isSM && topOverlayInset !== undefined;

  const edgePadding = useMemo(() => {
    if (shouldInsetMapForLocationContainer) return MAP_EDGE_PADDING_SM_UPDATE;

    if (shouldInsetMapForLocationHeader && topOverlayInset !== undefined) {
      return { ...MAP_EDGE_PADDING_XS, top: topOverlayInset + MAP_EDGE_SPACING_XS };
    }

    return match([MAP_EDGE_PADDING_XS, MAP_EDGE_PADDING_SM]);
  }, [match, shouldInsetMapForLocationContainer, shouldInsetMapForLocationHeader, topOverlayInset]);
  const controlsRightInset = shouldInsetMapForLocationContainer
    ? MAP_CONTROLS_RIGHT_INSET_SM_UPDATE
    : undefined;
  const controlsTopInset = shouldInsetMapForLocationHeader
    ? topOverlayInset + theme.spacing['4']
    : undefined;
  const searchAreaRightInset = shouldInsetMapForLocationContainer
    ? MAP_OVERLAY_RIGHT_INSET_SM_UPDATE
    : undefined;
  const searchAreaTopInset = shouldInsetMapForLocationHeader ? topOverlayInset : undefined;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container} onLayout={updateMapDimensions}>
      {isScreenFocused ? (
        <LocationsMap
          pins={pins}
          customerPositionPin={customerPositionPin}
          isAutoFitBoundsEnabled={isAutofitEnabled}
          isOffsetEnabled={isOffsetEnabled}
          edgePadding={edgePadding}
          controlsRightInset={controlsRightInset}
          controlsTopInset={controlsTopInset}
          withBlurredControls={isLocationSelectionUpdateEnabled}
          isLiquidGlassEnabled={isLiquidGlassEnabled}
          searchAreaRightInset={searchAreaRightInset}
          searchAreaTopInset={searchAreaTopInset}
          activePinId={isDeliveryLocationSearchType ? deliveryLocation?.id : focusedPinId} // prettier-ignore
          region={searchAreaRegion ?? region ?? defaultRegion}
          isLoadingMapLocations={isLoadingLocations}
          isSearchAreaBtnEnabled={locationSearchType !== 'delivery'}
          onRegionChange={setLocationSearchArea}
          onSearchAreaPress={handleOnSearchAreaPress}
          onGeolocateUserBtnPress={searchBasedOnCurrentPosition}
          onMapPress={clearFocusedPinId}
          onCalloutPress={onCalloutPress}
          onPinPress={setFocusedLocation}
        />
      ) : null}
    </View>
  );
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const US_REGION_XS = { lat: 37.0902, lng: -95.7129, zoom: 3.2 }; // for mobile
const US_REGION_SM = { lat: 37.0902, lng: -95.7129, zoom: 4 }; // for desktop

const MAP_EDGE_SPACING_XS = 50;
const MAP_EDGE_SPACING_SM = 40;

const MAP_EDGE_PADDING_XS: MapEdgePadding = {
  top: 100,
  bottom: 220,
  right: MAP_EDGE_SPACING_XS,
  left: MAP_EDGE_SPACING_XS,
};
const MAP_EDGE_PADDING_SM: MapEdgePadding = {
  top: MAP_EDGE_SPACING_SM,
  bottom: MAP_EDGE_SPACING_SM,
  right: MAP_EDGE_SPACING_SM,
  left: MAP_EDGE_SPACING_SM,
};

const LOCATION_CONTAINER_WIDTH_SM = 375;

const LOCATION_CONTAINER_RIGHT_SPACING_SM = theme.spacing['3'];
const MAP_OVERLAY_RIGHT_INSET_SM_UPDATE =
  LOCATION_CONTAINER_WIDTH_SM + LOCATION_CONTAINER_RIGHT_SPACING_SM;

const MAP_CONTROLS_RIGHT_INSET_SM_UPDATE = MAP_OVERLAY_RIGHT_INSET_SM_UPDATE + theme.spacing['6'];
const MAP_EDGE_PADDING_SM_UPDATE: MapEdgePadding = {
  ...MAP_EDGE_PADDING_SM,
  right: MAP_OVERLAY_RIGHT_INSET_SM_UPDATE + MAP_EDGE_SPACING_SM,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchMapProps = {
  locationSearchActorRef: LocationSearchService;
  topOverlayInset?: number;
};

type OnPinCalloutPress = NonNullable<ComponentProps<typeof LocationsMap>['onCalloutPress']>;
type PinLocationDetails = Parameters<OnPinCalloutPress>[0];
type MapEdgePadding = ComponentProps<typeof LocationsMap>['edgePadding'];
